/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useMemo, useCallback, useState, useEffect, createRef, useRef } from 'react';
// eslint-disable-next-line max-len
import { useGetMonthYearFromDateStringOrEmptyStartingMonth } from '../../../utils/hooks/useGetMonthYearFromDateStringOrEmptyStartingMonth.js';
import { useGetStartingFocusedDay } from '../../../utils/hooks/useGetStartingFocusedDay.js';
import {
  getSixBySevenCurrentMonthDaysMatrix,
  sumFromDateValues,
  sumValuesToDate,
  getDateStringFromDay,
} from '../../../utils/dateHelpers.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';
import type { DSControlledDateTimePickerInternalsT } from '../../../sharedTypes.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sliceIntoChunks<T = any>(arr: Array<T>, chunkSize: number): Array<Array<T>> {
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
}
interface UseCurrentDisplayedMonthYearLogicT {
  currentMonth: DSControlledDateTimePickerInternalsT.DateMonth;
  currentYear: number;
  daysArray: DSControlledDateTimePickerT.MetaMonthDay[];
  invisibleFirstDay?: DSControlledDateTimePickerT.MetaMonthDay;
  daysMatrixByWeekday: DSControlledDateTimePickerT.MetaMonthDay[][];
  invisibleLastDay?: DSControlledDateTimePickerT.MetaMonthDay;
  handlePrevMonth: () => void;
  handlePrevYear: () => void;
  handleNextMonth: () => void;
  handleNextYear: () => void;
  daysArraysRef: React.MutableRefObject<DSControlledDateTimePickerT.MetaMonthDay[]>;
  resetMonthYear: () => void;
}

const getDaysArrays = (currentMonth: DSControlledDateTimePickerInternalsT.DateMonth, currentYear: number) => {
  const newDaysArray = getSixBySevenCurrentMonthDaysMatrix(currentMonth, currentYear);
  const hiddenPrevDay = sumValuesToDate(newDaysArray[0].day, {
    daysToSum: -1,
  });
  const hiddenPrevMetaday = {
    day: hiddenPrevDay,
    dayAsString: getDateStringFromDay(hiddenPrevDay),
    isPrevMonthDay: true,
    isNextMonthDay: false,
    isCurrMonthDay: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
    isHidden: true,
  };
  const hiddenNextDay = sumValuesToDate(newDaysArray[newDaysArray.length - 1].day, {
    daysToSum: 1,
  });
  const hiddenNextMetaday = {
    day: hiddenNextDay,
    dayAsString: getDateStringFromDay(hiddenNextDay),
    isPrevMonthDay: false,
    isNextMonthDay: true,
    isCurrMonthDay: false,
    dayBtnRef: createRef<HTMLButtonElement>() as React.MutableRefObject<HTMLButtonElement>,
    isHidden: true,
  };
  return [hiddenPrevMetaday, ...newDaysArray, hiddenNextMetaday];
};
export const useCurrentDisplayedMonthYearLogic = (): UseCurrentDisplayedMonthYearLogicT => {
  const { month: monthFromDateStringFallback, year: yearFromDateStringFallback } =
    useGetMonthYearFromDateStringOrEmptyStartingMonth();
  const { startFocusedDate } = useGetStartingFocusedDay();

  /*
   * PUI-15900 https://jira.elliemae.io/browse/PUI-15900
   *
   * The displayed month must stay aligned with the day the calendar will focus on open
   * (see useFocusLogic / useGetStartingFocusedDay). When the input holds a partial date
   * (e.g. a month with no day/year), `useGetMonthYearFromDateStringOrEmptyStartingMonth`
   * would drive the grid to that partial month, while the starting focused day resolves
   * to `onCalendarOpenFocusedDay` — a day in a different month that is then not rendered,
   * so focus is lost.
   *
   * Whenever a starting focused day is resolved (a complete valid input date, or
   * `onCalendarOpenFocusedDay` when the input is incomplete) the grid follows THAT day's
   * month/year, so the focused day is always visible. Only when there is no starting
   * focused day do we fall back to the input/emptyPickerStartingMonth month.
   */
  const monthFromDateString = startFocusedDate
    ? (startFocusedDate.getMonth() as DSControlledDateTimePickerInternalsT.DateMonth)
    : monthFromDateStringFallback;
  const yearFromDateString = startFocusedDate ? startFocusedDate.getFullYear() : yearFromDateStringFallback;

  const [currentMonth, setCurrentMonth] = useState(monthFromDateString);
  const [currentYear, setCurrentYear] = useState(yearFromDateString);

  const daysArraysRef = useRef<DSControlledDateTimePickerT.MetaMonthDay[]>([]);

  const resetMonthYear = useCallback(() => {
    setCurrentMonth(monthFromDateString);
    setCurrentYear(yearFromDateString);
  }, [monthFromDateString, yearFromDateString]);
  // side-effects are really really controlled in this case so use-effect should be fine
  // don't add 200 side-effects to those or else Chaos would hunt us for the years to come
  useEffect(() => {
    setCurrentMonth(monthFromDateString);
  }, [monthFromDateString]);
  useEffect(() => {
    setCurrentYear(yearFromDateString);
  }, [yearFromDateString]);

  const daysArray = useMemo(() => getDaysArrays(currentMonth, currentYear), [currentMonth, currentYear]);

  daysArraysRef.current = daysArray;

  const handlePrevYear = useCallback(() => {
    const { year: newYear } = sumFromDateValues({ month: currentMonth, year: currentYear }, { yearsToSum: -1 });
    setCurrentYear(newYear);
  }, [currentMonth, currentYear]);

  const handlePrevMonth = useCallback(() => {
    const { month: newMonth, year: newYear } = sumFromDateValues(
      { month: currentMonth, year: currentYear },
      { monthsToSum: -1 },
    );
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    daysArraysRef.current = getDaysArrays(newMonth, newYear);
  }, [currentMonth, currentYear]);

  const handleNextMonth = useCallback(() => {
    const { month: newMonth, year: newYear } = sumFromDateValues(
      { month: currentMonth, year: currentYear },
      { monthsToSum: 1 },
    );
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    daysArraysRef.current = getDaysArrays(newMonth, newYear);
  }, [currentMonth, currentYear]);

  const handleNextYear = useCallback(() => {
    const { year: newYear } = sumFromDateValues({ month: currentMonth, year: currentYear }, { yearsToSum: 1 });
    setCurrentYear(newYear);
  }, [currentMonth, currentYear]);

  const { invisibleFirstDay, daysMatrixByWeekday, invisibleLastDay } = useMemo(() => {
    const [invisible1stDay, ...otherDays] = [...daysArray];
    const invisibleDayLast = otherDays.pop();
    return {
      invisibleFirstDay: invisible1stDay,
      daysMatrixByWeekday: sliceIntoChunks<DSControlledDateTimePickerT.MetaMonthDay>(otherDays, 7),
      invisibleLastDay: invisibleDayLast,
    };
  }, [daysArray]);

  return useMemo(
    () => ({
      currentMonth,
      currentYear,
      daysArray,
      invisibleFirstDay,
      daysMatrixByWeekday,
      invisibleLastDay,
      handlePrevYear,
      handlePrevMonth,
      handleNextMonth,
      handleNextYear,
      daysArraysRef,
      resetMonthYear,
    }),
    [
      currentMonth,
      currentYear,
      daysArray,
      daysMatrixByWeekday,
      handleNextMonth,
      handleNextYear,
      handlePrevMonth,
      handlePrevYear,
      invisibleFirstDay,
      invisibleLastDay,
      resetMonthYear,
    ],
  );
};
